import { Alert, Badge, Button, Card, TextInput, Grid, Group, Progress, SegmentedControl, Select, Stack, Text, Title } from "@mantine/core";
import {
  IconAlertTriangle, IconArrowRight, IconBellRinging, IconClipboardCheck, IconClockExclamation, IconLayersSubtract, IconSearch, IconBasket, IconCash, IconFlask, IconLeaf, IconMap2, IconShieldCheck, IconTrees, IconUsers,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { http } from "../api/http";
import { useCan, useUser } from "../auth/user";
import { Chart, pivot } from "../components/Charts";
import { ChoroplethMap } from "../components/ChoroplethMap";
import { DataTable } from "../components/DataTable";
import { fmtNum, KpiGrid, PageHeader, SEASONS } from "../components/ui";
import { useOpstina } from "../context/opstina";
import { useT } from "../i18n/script";
import { CaseStatusBadge } from "./cases/common";
import MyHoldingPage from "./cases/MyHoldingPage";
import StatsPage from "./public/StatsPage";

interface Overview { sezona: number; kpi: Record<string, number | null>; prethodna: Record<string, number | null> }
interface Compliance { id: number; name: string; species: string; producer: string; opstina: string; mandatory_total: number;
  mandatory_done: number; overdue: number }

function delta(cur: number | null | undefined, prev: number | null | undefined, t: (s: string) => string) {
  if (!cur || !prev) return undefined;
  const d = ((cur - prev) / prev) * 100;
  return <Text span size="xs" c={d >= 0 ? "green" : "red"}>{d >= 0 ? "▲" : "▼"} {fmtNum(Math.abs(d), 1)}% {t("u odnosu na prethodnu sezonu")}</Text>;
}

export default function DashboardPage() {
  const { t } = useT();
  const user = useUser();
  const can = useCan();
  const nav = useNavigate();
  const { opstina, setOpstina, unitName } = useOpstina();
  const [season, setSeason] = useState(String(new Date().getFullYear()));
  const [metric, setMetric] = useState("povrsina_ha");
  const [q, setQ] = useState("");
  const analytics = can("analytics:read");

  const ov = useQuery({ queryKey: ["overview", season], enabled: analytics,
    queryFn: () => http.get<Overview>("/api/v1/analytics/overview", { sezona: season }) });
  const choro = useQuery({ queryKey: ["choro", metric, season], enabled: analytics,
    queryFn: () => http.get<{ features: { properties: { code: string; name: string; value: number | null } }[]; label: string }>(
      "/api/v1/analytics/choropleth", { metric, sezona: season }) });
  const bySpecies = useQuery({ queryKey: ["by-vrsta", season], enabled: analytics,
    queryFn: () => http.get<{ rows: Record<string, unknown>[] }>("/api/v1/analytics/by",
      { dimension: "vrsta", sezona: season, metric: ["povrsina_ha", "ubrano_t", "prinos_t_ha"] }) });
  const ts = useQuery({ queryKey: ["ts-berba"], enabled: analytics,
    queryFn: () => http.get<{ rows: { period: string; serija: string; vrijednost: number }[] }>("/api/v1/analytics/timeseries",
      { metric: "berba_kg", interval: "mjesec", split: "vrsta" }) });
  const comp = useQuery({ queryKey: ["pheno-compliance", season], enabled: can("phenology:read"),
    queryFn: () => http.get<Compliance[]>("/api/v1/phenology/compliance", { season }) });
  const diary = useQuery({ queryKey: ["diary-summary", season], enabled: can("diary:read") && !analytics,
    queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/diary/summary", { season }) });
  const pred = useQuery({ queryKey: ["pred-sum", season], enabled: analytics,
    queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/predictions/summary", { sezona: season, dimension: "vrsta" }) });

  const k = ov.data?.kpi ?? {};
  const p = ov.data?.prethodna ?? {};
  const overdue = (comp.data ?? []).filter((c) => c.overdue > 0);
  const compPct = comp.data?.length
    ? (100 * comp.data.reduce((s, c) => s + c.mandatory_done, 0)) / Math.max(1, comp.data.reduce((s, c) => s + c.mandatory_total, 0)) : 0;
  const tsP = pivot(ts.data?.rows ?? []);

  if (!can("orchards:read")) {
    // eksterni korisnik (nosilac gazdinstva) bez verifikovanog gazdinstva: unos podataka i praćenje verifikacije;
    // javni korisnik (bez prava na zahtjev): samo javna statistika, bez ličnih podataka
    return can("access:request") ? <MyHoldingPage /> : <StatsPage embedded />;
  }

  return (
    <Stack>
      <PageHeader title={`Dobro došli, ${user.name || user.username}`}
        description={opstina ? `Prikaz za opštinu ${unitName(opstina)} (izbor u zaglavlju).` : "Prikaz za sve opštine u vašoj nadležnosti."}
        right={<Select w={110} data={SEASONS} value={season} onChange={(v) => v && setSeason(v)} aria-label={t("Sezona")} />} />

      {(can("cases:read") || can("layers:manage")) && <TasksPanel />}

      <Card withBorder padding="md" radius="lg" style={{ background: "linear-gradient(110deg, #1f4d36 0%, #2f6b45 60%, #5b8f3e 100%)" }}>
        <Group justify="space-between" wrap="wrap" gap="md">
          <div>
            <Title order={4} c="white">{t("Gazdinstva i voćnjaci")}</Title>
            <Text size="sm" c="#d6ebcf">{t("Pretraga po nazivu, BPG-u, nosiocu, sorti ili opštini; karton, mapa i 3D su u kartonu voćnjaka.")}</Text>
          </div>
          <TextInput size="md" w={{ base: "100%", sm: 420 }} leftSection={<IconSearch size={18} />} value={q} onChange={(e) => setQ(e.currentTarget.value)}
            onKeyDown={(e) => e.key === "Enter" && nav(`/gazdinstva?q=${encodeURIComponent(q)}`)} placeholder={t("Pronađi gazdinstvo ili voćnjak i pritisni Enter")} />
        </Group>
      </Card>

      {analytics && (
        <KpiGrid cols={4} items={[
          { label: "Voćnjaka", value: fmtNum(k.vocnjaka, 0), icon: <IconTrees />, onClick: () => nav("/gazdinstva?pogled=vocnjaci") },
          { label: "Proizvođača", value: fmtNum(k.proizvodjaca, 0), icon: <IconUsers />, onClick: () => nav("/gazdinstva") },
          { label: "Površina", value: fmtNum(k.povrsina_ha, 1), unit: "ha", icon: <IconMap2 /> },
          { label: "Stabala (UAV)", value: fmtNum(k.stabala, 0), icon: <IconLeaf /> },
          { label: "Ubrano", value: fmtNum(k.ubrano_t, 1), unit: "t", icon: <IconBasket />, color: "soil", hint: delta(k.ubrano_t, p.ubrano_t, t) },
          { label: "Prosječan prinos", value: fmtNum(k.prinos_t_ha, 1), unit: "t/ha", icon: <IconBasket />, color: "soil" },
          { label: "Prodaja", value: fmtNum(k.prodaja_km, 0), unit: "KM", icon: <IconCash />, color: "soil", hint: delta(k.prodaja_km, p.prodaja_km, t) },
          { label: "Tretmana FFS", value: fmtNum(k.tretmana, 0), icon: <IconFlask />, color: "grape" },
        ]} />
      )}

      {overdue.length > 0 && (
        <Alert color="orange" icon={<IconAlertTriangle />} title={t("Fenološke faze koje kasne")}>
          {t(`${overdue.length} voćnjaka nema upisane obavezne faze kojima je istekao rok.`)}{" "}
          <Text span c="blue" style={{ cursor: "pointer" }} onClick={() => nav("/fenologija")}>{t("Pregled")}</Text>
        </Alert>
      )}

      <Grid>
        {analytics && (
          <Grid.Col span={{ base: 12, lg: 7 }}>
            <Card withBorder>
              <Group justify="space-between" mb="xs">
                <Title order={5}>{t("Kartogram po opštinama")}</Title>
                <SegmentedControl size="xs" value={metric} onChange={setMetric} data={[
                  { value: "povrsina_ha", label: t("Površina") }, { value: "ubrano_t", label: t("Berba") },
                  { value: "prinos_t_ha", label: t("Prinos") }, { value: "fenologija_pct", label: t("Fenologija %") }]} />
              </Group>
              <ChoroplethMap data={choro.data} height={360} selected={opstina}
                onSelect={(c) => setOpstina(c === opstina ? null : c)} />
              <Text size="xs" c="dimmed" mt={4}>{t("Klik na opštinu postavlja filter u zaglavlju (ponovni klik ga uklanja).")}</Text>
            </Card>
          </Grid.Col>
        )}
        <Grid.Col span={{ base: 12, lg: analytics ? 5 : 12 }}>
          <Card withBorder h="100%">
            <Group justify="space-between"><Title order={5}>{t("Fenološki plan — ispunjenost obaveznih faza")}</Title>
              <Badge variant="light">{fmtNum(compPct, 0)}%</Badge></Group>
            <Progress value={compPct} mt="xs" mb="sm" color={compPct > 80 ? "green" : compPct > 50 ? "yellow" : "red"} />
            <DataTable rows={(comp.data ?? []).slice(0, 200) as unknown as Record<string, unknown>[]} search={false} maxHeight={330} dense
              onRowClick={(r) => nav(`/vocnjaci/${r.id}`)} columns={[
                { key: "name", label: "Voćnjak" }, { key: "opstina", label: "Opština" },
                { key: "mandatory_done", label: "Upisano", type: "integer", render: (r) => `${r.mandatory_done}/${r.mandatory_total}` },
                { key: "overdue", label: "Kasni", type: "integer", render: (r) => (Number(r.overdue) > 0 ? <Badge color="red" size="sm">{String(r.overdue)}</Badge> : "—") },
              ]} />
          </Card>
        </Grid.Col>
        {analytics && (
          <>
            <Grid.Col span={{ base: 12, lg: 6 }}>
              <Card withBorder>
                <Title order={5} mb="xs">{t("Berba po mjesecima i vrstama (kg)")}</Title>
                <Chart rows={tsP.rows} x="period" series={tsP.series} type="bar" />
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, lg: 6 }}>
              <Card withBorder>
                <Title order={5} mb="xs">{t("Površina i prinos po vrstama")}</Title>
                <Chart rows={bySpecies.data?.rows ?? []} x="naziv" series={[{ key: "povrsina_ha", label: t("Površina (ha)") },
                  { key: "prinos_t_ha", label: t("Prinos (t/ha)") }]} />
              </Card>
            </Grid.Col>
            <Grid.Col span={12}>
              <Card withBorder>
                <Group justify="space-between" mb="xs">
                  <Title order={5}>{t("Predikcija prinosa po vrstama")}</Title>
                  <Badge variant="light" leftSection={<IconShieldCheck size={12} />}>{t("model učen na istoriji u sistemu")}</Badge>
                </Group>
                <DataTable rows={pred.data ?? []} search={false} columns={[
                  { key: "naziv", label: "Vrsta" }, { key: "vocnjaka", label: "Voćnjaka", type: "integer" },
                  { key: "povrsina_ha", label: "Površina (ha)", type: "number" }, { key: "ocekivano_t", label: "Očekivano (t)", type: "number", decimals: 1 },
                  { key: "donja_t", label: "Donja granica (t)", type: "number", decimals: 1 }, { key: "gornja_t", label: "Gornja granica (t)", type: "number", decimals: 1 },
                ]} />
              </Card>
            </Grid.Col>
          </>
        )}
        {!analytics && diary.data && (
          <Grid.Col span={12}>
            <Card withBorder>
              <Title order={5} mb="xs">{t("Moji voćnjaci — poljoprivredna knjiga")}</Title>
              <DataTable rows={diary.data} onRowClick={(r) => nav(`/vocnjaci/${r.id}`)} columns={[
                { key: "name", label: "Voćnjak" }, { key: "species", label: "Vrsta" }, { key: "tretmana", label: "Tretmana", type: "integer" },
                { key: "dubrenja", label: "Đubrenja", type: "integer" }, { key: "navodnjavanja", label: "Navodnjavanja", type: "integer" },
                { key: "troskovi_km", label: "Troškovi (KM)", type: "money" }, { key: "posljednji_upis", label: "Posljednji upis", type: "date" },
              ]} />
            </Card>
          </Grid.Col>
        )}
      </Grid>
    </Stack>
  );
}


interface CaseRow { id: number; number: string; status: string; status_label: string; applicant_name: string | null; holding_name: string | null;
  bpg: string | null; opstina: string | null; due_date: string | null; overdue: boolean; assigned_name: string | null }

/** Zadaci zaposlenog: predmeti (dodjela, obrada, rokovi) i slojevi na odobrenju — prvo što vidi na početnoj. */
function TasksPanel() {
  const { t } = useT();
  const can = useCan();
  const nav = useNavigate();
  const stats = useQuery({ queryKey: ["cases-stats"], enabled: can("cases:read"), refetchInterval: 60_000,
    queryFn: () => http.get<Record<string, number>>("/api/v1/cases/stats") });
  const mine = useQuery({ queryKey: ["cases", "dash", can("cases:assign")], enabled: can("cases:read"),
    queryFn: () => http.get<CaseRow[]>("/api/v1/cases", { status: "otvoreni", assigned: can("cases:assign") ? "svi" : "moji", limit: 6 }) });
  const layers = useQuery({ queryKey: ["layers-pending"], enabled: can("layers:manage"),
    queryFn: () => http.get<{ id: number }[]>("/api/v1/layers/manage", { status: "predlozen" }) });
  const s = stats.data ?? {};
  const tiles = [
    can("cases:assign") && { label: "Čeka dodjelu", value: s.ceka_dodjelu, icon: <IconBellRinging size={20} />, color: "#c8453a", to: "/predmeti?v=ceka" },
    can("cases:read") && { label: "Moji otvoreni predmeti", value: s.moji, icon: <IconClipboardCheck size={20} />, color: "#1f4d36", to: "/predmeti?v=moji" },
    can("cases:read") && { label: "Preko roka", value: s.preko_roka, icon: <IconClockExclamation size={20} />, color: "#b7791f", to: "/predmeti?v=rok" },
    can("layers:manage") && { label: "Slojevi na odobrenju", value: layers.data?.length ?? 0, icon: <IconLayersSubtract size={20} />, color: "#2563eb", to: "/admin/slojevi" },
  ].filter(Boolean) as { label: string; value?: number; icon: React.ReactNode; color: string; to: string }[];
  return (
    <Card withBorder radius="lg" padding="md">
      <Group justify="space-between" mb="sm">
        <div>
          <Title order={5}>{t("Moji zadaci")}</Title>
          <Text size="xs" c="dimmed">{t("Zahtjevi za registraciju i verifikaciju gazdinstava i slojevi koji čekaju odluku")}</Text>
        </div>
        {can("cases:read") && <Button size="xs" variant="light" rightSection={<IconArrowRight size={14} />} onClick={() => nav("/predmeti")}>{t("Svi predmeti")}</Button>}
      </Group>
      <Grid>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Grid gap="xs">
            {tiles.map((x) => (
              <Grid.Col key={x.label} span={6}>
                <Card withBorder radius="md" padding="sm" className="holding-card" onClick={() => nav(x.to)} style={{ borderLeft: `4px solid ${x.color}` }}>
                  <Group justify="space-between" wrap="nowrap">
                    <div>
                      <Text fz={26} fw={800} lh={1.1} c={x.value ? x.color : "dimmed"}>{x.value ?? "—"}</Text>
                      <Text size="xs" c="dimmed">{t(x.label)}</Text>
                    </div>
                    <span style={{ color: x.color, opacity: 0.8 }}>{x.icon}</span>
                  </Group>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          {can("cases:read") && (!mine.data?.length
            ? <Text size="sm" c="dimmed" mt="md">{t("Nema otvorenih predmeta — sve je obrađeno.")}</Text>
            : (
              <Stack gap={6}>
                {mine.data.map((c) => (
                  <Group key={c.id} justify="space-between" wrap="nowrap" p={8} className="holding-card"
                    style={{ border: "1px solid var(--mantine-color-default-border)", borderRadius: 10, cursor: "pointer" }}
                    onClick={() => nav(`/predmeti/${c.id}`)}>
                    <div style={{ minWidth: 0 }}>
                      <Text size="sm" fw={650} truncate>{c.number} · {c.holding_name || c.applicant_name || "—"}</Text>
                      <Text size="xs" c="dimmed" truncate>{c.bpg ? `BPG ${c.bpg} · ` : ""}{c.opstina ?? ""}{c.assigned_name ? ` · ${c.assigned_name}` : ` · ${t("nije dodijeljen")}`}</Text>
                    </div>
                    <Group gap={6} wrap="nowrap">
                      {c.overdue && <Badge color="red" size="xs">{t("kasni")}</Badge>}
                      <CaseStatusBadge status={c.status} />
                    </Group>
                  </Group>
                ))}
              </Stack>
            ))}
        </Grid.Col>
      </Grid>
    </Card>
  );
}
